import { AddressDbService, EntityinfoAddressPayload } from "@app/address/store/services/address-db.service";
import { LOG_CATEGORY, LOG_SUB_TYPE, LOG_TYPE, createAuditLogKey } from "@app/audit-log/services/audit-log.constants";
import { EntityDbService, EntityWithAncestors } from "@app/entity/services/entity-db.service";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { BehaviorSubject, Observable, of, throwError } from "rxjs";
import { catchError, concatMap, exhaustMap, filter, map, mergeMap, switchMap, tap } from "rxjs/operators";
import { Project, ProjectData, ProjectHelper } from "../../model/project";
import { DataActions, ProjectActions } from "../actions";

import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { Address } from "@app/address/model/address";
import { AuditLogService } from "@app/audit-log/services/audit-log.service";
import { CustomerService } from "@app/customer/services/customer.service";
import { ALL_VIEW_COLUMNS } from "@app/project/constants/all-view-columns.constants";
import { ClareSystemHelper } from "@app/project/model/clare-system";
import { accountRoutePaths } from "@app/project/project-routing-constants";
import { CHDeviceService } from "@app/project/services/ch-device.service";
import { ClareSystemDbService } from "@app/project/services/clare-system-db.service";
import { ProjectDbService } from "@app/project/services/project-db.service";
import { ProjectHelperService } from "@app/project/services/project-helper.service";
import { ProjectListItemDbService } from "@app/project/services/project-list-item-db.service";
import { ProjectService } from "@app/project/services/project.service";
import { ServicePlansService } from "@app/project/services/service-plans.service";
import { emptyAction } from "@app/root-store/actions/snackbar.action";
import { DeleteConfirmationService } from "@app/shared/components/delete-confirmation/delete-confirmation.service";
import { RalinkDatabaseService } from "@app/shared/databases/ralink.database";
import { UserManagementActions } from "@app/user/store";
import { Action } from "@ngrx/store";
import { ActionWord } from "../../../shared/resources/action-word";
import { DataServiceError } from "../../../shared/services/data-service-error";
import { UpdateProjectController } from "../actions/projects.actions";
import { ProjectDispatchers } from "../dispatchers/project.dispatchers";

const toAction = DataActions.toAction();
type ProjectAction = ProjectActions.ProjectAction;

@Injectable()
export class ProjectEffects {
    listRoutePath = `/${accountRoutePaths.root}${accountRoutePaths.list}`;
    private projectHelper = new ProjectHelper();
    constructor(
        private actions$: Actions,
        private addressDbService: AddressDbService,
        private clareSystemDbService: ClareSystemDbService,
        private projectListItemDbService: ProjectListItemDbService,
        private router: Router,
        private projectDbService: ProjectDbService,
        private entityDbService: EntityDbService,
        private deleteConfirmationService: DeleteConfirmationService,
        private ralinkDatabaseService: RalinkDatabaseService,
        private customerService: CustomerService,
        private readonly projectDispatchers: ProjectDispatchers,
        private readonly projectService: ProjectService,
        private readonly deviceService: CHDeviceService,
        private readonly servicePlanService: ServicePlansService,
        private readonly auditLogService: AuditLogService
    ) {}

    ngrxOnInitEffects() {
        return ProjectActions.setProjectListEntity();
    }

    setLocalStorage$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ProjectActions.setProjectListEntity),
            switchMap(() => {
                const selectedEntityId = window.localStorage.getItem("selectedEntity");
                return of(new ProjectActions.SetSelectedEntitySuccess(selectedEntityId ? +selectedEntityId : undefined));
            })
        )
    );

    createProject$: Observable<Action> = createEffect(() =>
        this.actions$.pipe(
            ofType(ProjectActions.ActionTypes.CreateProject),
            map((action: ProjectActions.CreateProject) => action.payload),
            exhaustMap(payload =>
                this.projectDbService.saveCustomerDetails(payload.customer).pipe(
                    exhaustMap(customer => {
                        payload.customerCreatedEmitter.emit(customer);
                        const address = payload.address;
                        const theCustomer = customer || payload.customer;
                        if (theCustomer?.id) {
                            const theProject = payload.project$.value;
                            theProject.entityId = theCustomer.entityId;
                            theProject.userinfo1 = theCustomer.id;
                            const projectData = this.projectHelper.toDbObject(theProject);
                            return this.createDbProject(projectData, payload.project$, address).pipe(
                                tap(result => {
                                    this.createAddressEntityInfos({ ...address, id: result?.payload?.addressId });
                                })
                            );
                        }

                        let err = "There was a problem saving the ";
                        if (!address || !address.id) {
                            err += "address ";
                        }

                        if ((!address || !address.id) && (!theCustomer || !theCustomer.id)) {
                            err += "and the ";
                        }

                        if (!theCustomer || !theCustomer.id) {
                            err += "customer";
                        }
                        throw new Error(err);
                    })
                )
            ),
            catchError(error => {
                console.log(error);
                return of(new ProjectActions.CreateProjectFailure(error));
            })
        )
    );

    createProjectNoCustomer$: Observable<Action> = createEffect(() =>
        this.actions$.pipe(
            ofType(ProjectActions.ActionTypes.CreateProjectNoCustomer),
            map((action: ProjectActions.CreateProjectNoCustomer) => action.payload),
            exhaustMap(payload => {
                const address = payload.address;

                const theProject = payload.project$.value;

                theProject.addressId = address.id;
                theProject.address = address;
                theProject.entityId = payload.entityId;
                theProject.userinfo1 = 0;

                const projectData = this.projectHelper.toDbObject(theProject);

                return this.createDbProject(projectData, payload.project$, address).pipe(
                    tap(result => {
                        this.createAddressEntityInfos({ ...address, id: result?.payload?.addressId });
                    })
                );
            }),
            catchError(error => {
                console.log(error);
                return of(new ProjectActions.CreateProjectFailure(error));
            })
        )
    );

    private createDbProject(
        projectData: ProjectData,
        payloadProject$: BehaviorSubject<Project>,
        address?: Address
    ): Observable<ProjectActions.CreateProjectSuccess> {
        return this.projectDbService.createProject$(projectData, address).pipe(
            map((newProject: ProjectData): any => {
                const project = this.projectHelper.fromDbObject(newProject);
                payloadProject$.next(project);
                return new ProjectActions.CreateProjectSuccess(project);
            })
        );
    }

    private createAddressEntityInfos(address: Address) {
        const addressEntityInfos: EntityinfoAddressPayload[] = [];
        if (address) {
            if (address.builderid) {
                addressEntityInfos.push({ address_id: address.id, entityinfo_id: +address.builderid });
            }
            if (address.communityid) {
                addressEntityInfos.push({ address_id: address.id, entityinfo_id: +address.communityid });
            }
            if (address.typeid) {
                addressEntityInfos.push({ address_id: address.id, entityinfo_id: +address.typeid });
            }
        }
        addressEntityInfos.forEach(info => {
            this.addressDbService.createEntityinfoAddress(info);
        });
    }

    createProjectFailure$: Observable<Action> = createEffect(() =>
        this.actions$.pipe(
            ofType(ProjectActions.ActionTypes.CreateProjectFailure),
            map(DataActions.handleErrorMessage(ActionWord.saved, "project"))
        )
    );

    createProjectController$: Observable<Action> = createEffect(() =>
        this.actions$.pipe(
            ofType(ProjectActions.ActionTypes.CreateProjectController),
            exhaustMap((action: ProjectActions.CreateProjectController) => {
                if (!action.project || !action.project.id) {
                    throw new Error("The controller was not saved. Invalid project ID.");
                }
                return this.projectDbService.upsertController(action).pipe(
                    switchMap(project => {
                        if (action?.customer) {
                            this.customerService.assignUserToClareSystem(action.customer, project, action.entity);
                        }

                        if (action.address?.coordinates && project.claresystem) {
                            return this.projectService
                                .sendCoordinatesToClareHome(project.claresystem.uuid, action.address, project)
                                .pipe(concatMap(() => of(project)));
                        }

                        return of(project);
                    }),
                    switchMap(project => {
                        action.closeDialog?.emit();
                        if (action.isNewProject) {
                            if (ProjectHelperService.isClareOne(project.claresystem.uuid)) {
                                const logKey = createAuditLogKey(LOG_CATEGORY.ACCOUNT, LOG_TYPE.ACCOUNT, LOG_SUB_TYPE.ADD_CLAREONE);
                                this.auditLogService.logEvent(logKey, {
                                    data: "Clareone Added",
                                    entityId: action.entity?.id,
                                    projectId: project.id,
                                    optionalParams: { uuid: project.claresystem.uuid },
                                });
                            }
                            return of(DataActions.handleSuccessMessage2(ActionWord.saved, null, "Account")());
                        }
                        return of(new ProjectActions.UpdateProjectSuccess(project));
                    }),
                    catchError(e => {
                        console.log(e);
                        return of(new ProjectActions.CreateProjectControllerFailure(e));
                    })
                );
            }),
            catchError(DataServiceError.handleError(this.projectHelper))
        )
    );

    updateProjectController$: Observable<Action> = createEffect(() =>
        this.actions$.pipe(
            ofType(ProjectActions.ActionTypes.UpdateProjectController),
            exhaustMap((action: ProjectActions.UpdateProjectController) => {
                if (!action.project || !action.project.id) {
                    throw new Error("The controller was not saved. Invalid project ID.");
                }
                return this.clareSystemDbService.updateClareSystem(action.claresystem).pipe(
                    exhaustMap(() => this.upsertSecuritySystem(action)),
                    catchError(DataServiceError.handleError(action.project))
                );
            }),
            catchError(DataServiceError.handleError(this.projectHelper))
        )
    );

    private upsertSecuritySystem(action: UpdateProjectController) {
        return this.projectDbService.upsertSecuritySystem(action.securitysystem, action.project).pipe(
            exhaustMap(securitySystem =>
                DataActions.toAction(new ProjectActions.ClearProjectStatuses())(
                    this.projectDbService.updateProject(action.project, action.claresystem, securitySystem),
                    ProjectActions.UpdateProjectSuccess,
                    ProjectActions.CreateProjectControllerFailure
                )
            ),
            catchError(e => {
                console.log(e);
                return of(new ProjectActions.CreateProjectControllerFailure(e));
            })
        );
    }

    upsertSecuritySystem$: Observable<Action> = createEffect(() =>
        this.actions$.pipe(
            ofType(ProjectActions.ActionTypes.UpsertSecuritySystem),
            exhaustMap((action: ProjectActions.UpsertSecuritySystem) => {
                if (!action.payload || !action.payload.project || !action.payload.securitysystem) {
                    throw new Error("The information was not saved. The account or security system info was missing.");
                }
                return this.projectDbService.upsertSecuritySystem(action.payload.securitysystem, action.payload.project).pipe(
                    exhaustMap(securitySystem => {
                        if (!action.payload.project.securitysystem?.id) {
                            return DataActions.toAction()(
                                this.projectDbService.updateProject(action.payload.project, null, securitySystem),
                                ProjectActions.UpdateProjectSuccess,
                                ProjectActions.CreateProjectControllerFailure,
                                action.payload.callbackEmitter
                            );
                        }
                        action.payload.project.securitysystem = securitySystem;
                        action.payload.callbackEmitter?.emit(new ProjectActions.UpdateProjectSuccess(action.payload.project));
                        return of(emptyAction());
                    }),
                    catchError(e => {
                        const errAction = new ProjectActions.UpdateProjectFailure(new DataServiceError(e, action.payload.project));
                        action.payload.callbackEmitter?.emit(errAction);
                        return of(emptyAction());
                    })
                );
            }),
            catchError(_ => of(emptyAction()))
        )
    );

    createProjectControllerFailure$: Observable<Action> = createEffect(() =>
        this.actions$.pipe(
            ofType(ProjectActions.ActionTypes.CreateProjectControllerFailure),
            map(DataActions.handleErrorMessage(ActionWord.saved, "controller"))
        )
    );

    getProjects$: Observable<Action> = createEffect(() =>
        this.actions$.pipe(
            ofType(ProjectActions.ActionTypes.GetProjects),
            map((action: ProjectActions.GetProjects) => action),
            switchMap(action =>
                this.projectListItemDbService.getProjectList(
                    action.payload.entityId,
                    action.payload.projectView.viewData.flatMap(viewKey => {
                        const col = Object.values(ALL_VIEW_COLUMNS).find(c => c.viewKey === viewKey);
                        if (!col) {
                            console.error(`No column found for viewKey=${viewKey}`);
                            return [];
                        }

                        return [col];
                    })
                )
            ),
            map(projs => projs || []),
            tap(projs => projs.forEach(proj => (proj.isOccupied = !!proj.userinfo1Id))),
            map(projects => new ProjectActions.GetProjectsSuccess({ projects })),
            catchError(DataActions.toError(ProjectActions.GetProjectsFailure))
        )
    );

    getProjectUsers$: Observable<any> = createEffect(() =>
        this.actions$.pipe(
            ofType(ProjectActions.ActionTypes.GetProjectUsers),
            switchMap((action: ProjectActions.GetProjectUsers) =>
                this.projectDbService.getProjectUsers$(action.payload).pipe(
                    map(result => {
                        if (result[0]) {
                            return new ProjectActions.GetProjectUsersSuccess({
                                projectId: action.payload,
                                users: result[0],
                            });
                        }
                    }),
                    filter(available => !!available)
                )
            )
        )
    );

    getCentralStationProjectData$: Observable<any> = createEffect(() =>
        this.actions$.pipe(
            ofType(ProjectActions.ActionTypes.GetCentralStationProjectData),
            switchMap((action: ProjectActions.GetCentralStationProjectData) =>
                this.projectDbService.getCentralStationProjectData(action.payload)
            ),
            map(
                centralStationProjects =>
                    new ProjectActions.GetCentralStationProjectDataSuccess({
                        centralStationProjects,
                    })
            )
        )
    );

    updateProjectDealer$: Observable<any> = createEffect(() =>
        this.actions$.pipe(
            ofType(ProjectActions.ActionTypes.UpdateProjectDealer),
            map((action: ProjectActions.UpdateProjectDealer) => action.payload),
            switchMap(payload =>
                this.projectDbService.updateProject$(payload.projectData).pipe(
                    map(() => {
                        payload.closeDialog.emit(payload);
                        if (payload.logData) {
                            const logKey = createAuditLogKey(LOG_CATEGORY.ACCOUNT, LOG_TYPE.ACCOUNT, LOG_SUB_TYPE.MOVE);
                            this.auditLogService.logEvent(logKey, payload.logData);
                        }
                        return new ProjectActions.UpdateProjectDealerSuccess();
                    }),
                    catchError(error => {
                        if (payload.logData) {
                            const logKey = createAuditLogKey(LOG_CATEGORY.ACCOUNT, LOG_TYPE.ACCOUNT, LOG_SUB_TYPE.MOVE);
                            this.auditLogService.logEvent(logKey, payload.logData);
                        }
                        return of(new ProjectActions.UpdateProjectDealerFailure(error));
                    })
                )
            ),
            catchError(error => of(new ProjectActions.UpdateProjectDealerFailure(error)))
        )
    );

    updateProjectDealerSuccess$: Observable<Action> = createEffect(() =>
        this.actions$.pipe(
            ofType(ProjectActions.ActionTypes.UpdateProjectDealerSuccess),
            map(DataActions.handleSuccessMessage(ActionWord.moved, null, ProjectHelper.dbTypeName))
        )
    );

    updateProject$: Observable<any> = createEffect(() =>
        this.actions$.pipe(
            ofType(ProjectActions.ActionTypes.UpdateProject),
            map((action: ProjectActions.UpdateProject) => action.payload),
            switchMap(payload =>
                this.projectDbService.updateProject$(payload.projectData).pipe(
                    map(() => {
                        if (payload.closeDialog) {
                            payload.closeDialog.emit(payload);
                            return emptyAction();
                        }

                        return new ProjectActions.UpdateProjectSuccess(payload.projectData);
                    }),
                    catchError(error => of(this.closeUpdateProjectDialog(payload, error)))
                )
            ),
            catchError(error => of(new ProjectActions.UpdateProjectFailure(error)))
        )
    );

    private closeUpdateProjectDialog(payload, error) {
        if (payload.closeDialog) {
            payload.closeDialog.emit();
        }
        return new ProjectActions.UpdateProjectFailure(error);
    }

    updateProjectSuccess$: Observable<Action> = createEffect(() =>
        this.actions$.pipe(ofType(ProjectActions.ActionTypes.UpdateProjectSuccess), map(DataActions.handleSuccessMessage(ActionWord.saved)))
    );

    updateProjectFailure$: Observable<Action> = createEffect(() =>
        this.actions$.pipe(
            ofType(ProjectActions.ActionTypes.UpdateProjectFailure),
            map(DataActions.handleErrorMessage(ActionWord.saved, "account"))
        )
    );

    goToProjects$: Observable<any> = createEffect(
        () =>
            this.actions$.pipe(
                ofType(ProjectActions.ActionTypes.GoToProjects),
                map(() => this.router.navigate(["/accounts"]))
            ),
        { dispatch: false }
    );

    createProjectUser$: Observable<Action> = createEffect(() =>
        this.actions$.pipe(
            ofType(ProjectActions.ActionTypes.CreateProjectUser),
            switchMap((action: ProjectActions.CreateProjectUser) => {
                const userInfoKey = action.payload.userInfoKey;
                const project = action.payload.project;

                return this.projectDbService.updateProject$({ ...project, [userInfoKey]: action.payload.userData.id }).pipe(
                    mergeMap(() => {
                        const newProject = Object.assign(this.projectHelper, project, { isOccupied: true });
                        newProject[userInfoKey] = action.payload.userData.id;
                        action.closeDialog.emit(action.payload);
                        return [
                            UserManagementActions.UserActions.upsertSuccessSilent({ user: action.payload.userData }),
                            new ProjectActions.CreateProjectUserSuccess(newProject),
                        ];
                    }),
                    catchError(error => {
                        console.error(error);
                        action.closeDialog.emit(null);
                        return of(new ProjectActions.CreateProjectUserFailure(error));
                    })
                );
            })
        )
    );

    upsertFailure$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ProjectActions.ActionTypes.CreateProjectUserFailure),
            map(DataActions.handleErrorMessage2(ActionWord.saved, "customer"))
        )
    );

    createOrDeleteProjectUserSuccess$: Observable<Action> = createEffect(() =>
        this.actions$.pipe(
            ofType(
                ProjectActions.ActionTypes.CreateProjectUserSuccess,
                ProjectActions.ActionTypes.DeleteProjectUserSuccess,
                ProjectActions.ActionTypes.UpdateProjectUserSuccess
            ),
            map(DataActions.handleSuccessMessage(ActionWord.saved))
        )
    );

    deleteProject$: Observable<Action> = createEffect(() =>
        this.actions$.pipe(
            ofType(ProjectActions.ActionTypes.DeleteProject),
            switchMap((action: ProjectAction) =>
                toAction(
                    this.projectDbService.deleteProject$(action.payload),
                    ProjectActions.DeleteProjectSuccess,
                    ProjectActions.DeleteProjectFailure
                )
            )
        )
    );

    deleteProjectUserConfirmation$: Observable<Action | Action[]> = createEffect(() =>
        this.actions$.pipe(
            ofType(ProjectActions.ActionTypes.DeleteProjectUserConfirmation),
            exhaustMap((action: ProjectActions.DeleteProjectUserConfirmation) =>
                this.deleteConfirmationService.open(
                    action.payload.userData,
                    ProjectActions.DeleteProjectUserConfirmationDismiss,
                    ProjectActions.DeleteProjectUser
                )
            )
        )
    );

    deleteProjectUser$: Observable<Action> = createEffect(() =>
        this.actions$.pipe(
            ofType(ProjectActions.ActionTypes.DeleteProjectUser),
            exhaustMap((action: ProjectActions.DeleteProjectUser) =>
                toAction(
                    this.projectDbService
                        .updateProject$({
                            ...action.payload.project,
                            [action.payload.userInfoKey]: null,
                        })
                        .pipe(
                            map(() => {
                                const newProject = Object.assign(this.projectHelper, action.payload.project);
                                newProject[action.payload.userInfoKey] = null;
                                action.closeDialog.emit();
                                return newProject;
                            })
                        ),
                    ProjectActions.DeleteProjectUserSuccess,
                    ProjectActions.DeleteProjectUserFailure
                )
            )
        )
    );

    deleteProjectSuccess$: Observable<Action> = createEffect(() =>
        this.actions$.pipe(
            ofType(ProjectActions.ActionTypes.DeleteProjectSuccess),
            tap(_ => {
                this.servicePlanService.clearActiveSubscription();
                this.projectService.clearActiveProject();
            }),
            map(DataActions.handleSuccessMessage(ActionWord.deleted, this.listRoutePath))
        )
    );

    deleteProjectConfirmation$: Observable<Action | Action[]> = createEffect(() =>
        this.actions$.pipe(
            ofType(ProjectActions.ActionTypes.DeleteProjectConfirmation),
            exhaustMap((action: ProjectActions.DeleteProjectConfirmation) =>
                this.deleteConfirmationService.open(
                    action.payload,
                    ProjectActions.DeleteProjectConfirmationDismiss,
                    ProjectActions.DeleteProject,
                    "this account",
                    undefined,
                    undefined,
                    action.confirmation
                )
            )
        )
    );

    updateProjectStatuses$: Observable<Action> = createEffect(() =>
        this.actions$.pipe(
            ofType(ProjectActions.ActionTypes.UpdateProjectStatuses),
            switchMap((action: ProjectActions.UpdateProjectStatuses) =>
                toAction(
                    this.ralinkDatabaseService.getClareSystem$(action.payload.uuids),
                    ProjectActions.UpdateProjectStatusesSuccess,
                    ProjectActions.UpdateProjectStatusesFailure
                )
            )
        )
    );

    getActiveProjectStatus$: Observable<Action> = createEffect(() =>
        this.actions$.pipe(
            ofType(ProjectActions.ActionTypes.GetActiveProjectStatus),
            switchMap((action: ProjectActions.GetActiveProjectStatus) =>
                toAction(
                    action.payload.uuid ? this.ralinkDatabaseService.getClareSystem$([action.payload.uuid]) : of(null),
                    ProjectActions.GetActiveProjectStatusSuccess,
                    ProjectActions.GetActiveProjectStatusFailure
                )
            )
        )
    );

    getProjectsUUIDs$: Observable<Action> = createEffect(() =>
        this.actions$.pipe(
            ofType(ProjectActions.ActionTypes.GetProjectsUUIDs),
            switchMap((action: ProjectActions.GetProjectsUUIDs) =>
                this.projectDbService.getEntityUuids$(action.payload).pipe(
                    map(result => new ProjectActions.GetProjectsUUIDsSuccess(result)),
                    filter(projectAction => !!projectAction)
                )
            )
        )
    );

    getActiveProject$: Observable<Action> = createEffect(() =>
        this.actions$.pipe(
            ofType(ProjectActions.ActionTypes.GetActiveProject),
            switchMap((action: ProjectActions.GetActiveProject) =>
                this.projectDbService.getProjectAndRelated$(action.payload).pipe(
                    map(projectAndUser => {
                        if (projectAndUser) {
                            const { primaryUser, ...project } = projectAndUser;
                            this.projectDispatchers.dispatch(
                                UserManagementActions.UserActions.loadActiveProjectUserSuccess({ user: primaryUser })
                            );
                            return new ProjectActions.GetActiveProjectSuccess(project);
                        }

                        return new ProjectActions.GetActiveProjectFailure(new DataServiceError(new Error("Project not found"), null));
                    })
                )
            )
        )
    );

    getActiveProjectByUuid$: Observable<Action> = createEffect(() =>
        this.actions$.pipe(
            ofType(ProjectActions.ActionTypes.GetActiveProjectByUuid),
            switchMap((action: ProjectActions.GetActiveProjectByUuid) =>
                this.projectDbService.getProjectAndRelatedByUuid(action.payload).pipe(
                    map(result => {
                        if (!result) {
                            return new ProjectActions.GetActiveProjectSuccess({
                                claresystem: ClareSystemHelper.getDefault(),
                                address: {},
                                securitysystem: null,
                                isOccupied: true,
                            } as Project);
                        }

                        const { address, clareSystem, project, securitysystem } = result;
                        const actionPayload: Project = {
                            ...project,
                            address: address || ({} as Address),
                            claresystem: clareSystem,
                            securitysystem,
                            isOccupied: project.userinfo1 !== 0,
                        };

                        return new ProjectActions.GetActiveProjectSuccess(actionPayload);
                    })
                )
            )
        )
    );

    GetActivePanelSummary$: Observable<Action> = createEffect(() =>
        this.actions$.pipe(
            ofType(ProjectActions.ActionTypes.GetActivePanelSummary),
            switchMap((action: ProjectActions.GetActivePanelSummary) =>
                toAction(
                    this.deviceService.getClareSummaryInfo(action.payload.uuid),
                    ProjectActions.GetActivePanelSummarySuccess,
                    ProjectActions.GetActivePanelSummaryFailure
                )
            )
        )
    );

    getProjectEntity$: Observable<Action> = createEffect(() =>
        this.actions$.pipe(
            ofType(ProjectActions.ActionTypes.GetActiveProjectEntity),
            switchMap((action: ProjectActions.GetActiveProjectEntity) =>
                toAction(
                    this.getEntityWithAncestor(action.payload).pipe(map(({ entity }) => entity)),
                    ProjectActions.GetActiveProjectEntitySuccess,
                    ProjectActions.GetActiveProjectEntityFailure
                )
            )
        )
    );

    private getEntityWithAncestor = (id: number): Observable<{ entity: EntityWithAncestors }> =>
        this.entityDbService.getEntityWithAncestor(id);

    setSelectedEntity$: Observable<Action> = createEffect(() =>
        this.actions$.pipe(
            ofType(ProjectActions.ActionTypes.SetSelectedEntity),
            switchMap((action: ProjectActions.SetSelectedEntity) => {
                window.localStorage.setItem("selectedEntity", action.payload?.toString());
                return of(new ProjectActions.SetSelectedEntitySuccess(action.payload));
            })
        )
    );

    activateOrSuspendProject$: Observable<any> = createEffect(() =>
        this.actions$.pipe(
            ofType(ProjectActions.ActionTypes.ActiveOrSuspendProject),
            map((action: ProjectActions.ActiveOrSuspendProject) => action.payload),
            switchMap(payload =>
                // Fetch latest data here since the caller only has a partial Project (ProjectListItem)
                this.projectDbService.getProjectAndRelated$(payload.projectId).pipe(
                    // Send update
                    exhaustMap(project => {
                        if (!project) {
                            console.error("No project found", payload);
                            throw Error("No project found");
                        }

                        const update = { ...project, inactive: !payload.doActivate };
                        const source$ = payload.doActivate
                            ? this.projectDbService.activateProject$(project.id)
                            : this.projectDbService.deactivateProject$(project.id);
                        return source$.pipe(
                            map(() => update),
                            catchError(error => {
                                console.error(error);
                                payload.closeDialog.emit(false);
                                return throwError(() => error);
                            })
                        );
                    }),
                    // Emit success / failure
                    map(project => {
                        if (payload.closeDialog) {
                            payload.closeDialog.emit(true);
                        }
                        if (!payload.doActivate) {
                            const logKey = createAuditLogKey(LOG_CATEGORY.ACCOUNT, LOG_TYPE.ACCOUNT, LOG_SUB_TYPE.SUSPEND);
                            this.auditLogService.logEvent(logKey, {
                                data: `Account Suspended`,
                                entityId: project.entityId,
                                projectId: project.id,
                                optionalParams: { uuid: project.claresystem?.uuid },
                            });
                        } else {
                            const logKey = createAuditLogKey(LOG_CATEGORY.ACCOUNT, LOG_TYPE.ACCOUNT, LOG_SUB_TYPE.ACTIVATE);
                            this.auditLogService.logEvent(logKey, {
                                data: `Account Activated`,
                                optionalParams: { uuid: project.claresystem?.uuid },
                            });
                        }
                        return new ProjectActions.ActiveOrSuspendProjectSuccess(payload.projectId, !payload.doActivate);
                    }),
                    catchError(error => {
                        payload.closeDialog.emit(false);
                        return of(this.closeUpdateProjectDialog(payload, error));
                    })
                )
            ),
            catchError(error => of(new ProjectActions.UpdateProjectFailure(error)))
        )
    );

    activeOrSuspendProjectSuccess$: Observable<Action> = createEffect(() =>
        this.actions$.pipe(
            ofType(ProjectActions.ActionTypes.ActiveOrSuspendProjectSuccess),
            map(DataActions.handleSuccessMessage2(ActionWord.saved, null, ProjectHelper.dbTypeName))
        )
    );
}
